import { useState, useCallback } from 'react';
import { validateField, validateForm } from '../utils/validation';

export const useForm = (initialValues, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setValue = useCallback((name, value) => {
    setValues(prev => ({
      ...prev,
      [name]: value
    }));

    // Always validate when setting a value to ensure errors are properly cleared
    if (validationRules[name]) {
      const error = validateField(value, validationRules[name]);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    }
  }, [validationRules]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setValue(name, value);
    
    // Immediately validate and clear errors if the field is valid
    if (validationRules[name]) {
      const error = validateField(value, validationRules[name]);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[name] = error;
        } else {
          delete newErrors[name];
        }
        return newErrors;
      });
    }
  }, [setValue, validationRules]);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));

    if (validationRules[name]) {
      const error = validateField(values[name], validationRules[name]);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  }, [values, validationRules]);

  const validateAllFields = useCallback(() => {
    const allErrors = validateForm(values, validationRules);
    setErrors(allErrors);
    return Object.keys(allErrors).length === 0;
  }, [values, validationRules]);

  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  const handleSubmit = useCallback((onSubmit) => async (e) => {
    e.preventDefault();
    
    setTouched(Object.keys(validationRules).reduce((acc, key) => ({
      ...acc,
      [key]: true
    }), {}));

    const isValid = validateAllFields();
    
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validationRules, validateAllFields]);

  // Calculate isValid based on current values and validation rules
  // This ensures that when values are set externally (like loading profile data),
  // the form validity is properly calculated
  const calculateIsValid = () => {
    // If no validation rules, form is valid
    if (Object.keys(validationRules).length === 0) return true;
    
    // Check if all required fields have values and no errors
    for (const [fieldName, rules] of Object.entries(validationRules)) {
      const value = values[fieldName];
      const error = validateField(value, rules);
      if (error) return false;
    }
    return true;
  };

  return {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    setValue,
    setValues,
    resetForm,
    isValid: calculateIsValid()
  };
};